import { useCallback, useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { CaretIcon } from '../assets/icons';
import { Spinner } from './ui';
import { useAppDispatch } from '../app/hooks';
import {
  EC_LEVEL,
  canHaveChildren,
  fetchConstituencyChildren,
  type TreeNode,
} from '../features/territory/constituencyTree';

/** A node is pickable when it maps to an EC level and carries a real area code. */
const isSelectable = (node: TreeNode) => Boolean(EC_LEVEL[node.kind] && node.areaCode);

/**
 * A lazy constituency tree seeded from given roots (the org's boundary nodes). Levels
 * below each root load on demand from the territory API — so a selected Parliament
 * expands into its Assemblies → Rural/Urban → … → Booth. Single-select.
 *
 * `expanded` + `childrenMap` are controlled by the parent so the drilled path and the
 * selection survive the dropdown closing/reopening.
 */
export default function ConstituencyTreeSelect({
  roots,
  selectedAreaCode,
  onSelect,
  expanded,
  setExpanded,
  childrenMap,
  setChildrenMap,
}: {
  roots: TreeNode[];
  /** The area code of the currently-selected node (for highlighting). */
  selectedAreaCode?: string;
  onSelect: (node: TreeNode) => void;
  expanded: Set<string>;
  setExpanded: Dispatch<SetStateAction<Set<string>>>;
  childrenMap: Record<string, TreeNode[]>;
  setChildrenMap: Dispatch<SetStateAction<Record<string, TreeNode[]>>>;
}) {
  const dispatch = useAppDispatch();
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());

  const loadChildren = useCallback(
    async (node: TreeNode) => {
      setLoadingIds((s) => new Set(s).add(node.id));
      setErrorIds((s) => {
        const n = new Set(s);
        n.delete(node.id);
        return n;
      });
      try {
        const kids = await fetchConstituencyChildren(dispatch, node);
        setChildrenMap((m) => ({ ...m, [node.id]: kids }));
      } catch {
        setErrorIds((s) => new Set(s).add(node.id));
      } finally {
        setLoadingIds((s) => {
          const n = new Set(s);
          n.delete(node.id);
          return n;
        });
      }
    },
    [dispatch, setChildrenMap],
  );

  // Auto-expand each root so its children (e.g. the PC's assemblies) load immediately.
  useEffect(() => {
    roots.forEach((r) => {
      if (canHaveChildren(r)) {
        setExpanded((s) => (s.has(r.id) ? s : new Set(s).add(r.id)));
        if (!childrenMap[r.id] && !loadingIds.has(r.id)) void loadChildren(r);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots]);

  const toggleExpand = (node: TreeNode) => {
    const willOpen = !expanded.has(node.id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (willOpen) next.add(node.id);
      else next.delete(node.id);
      return next;
    });
    if (willOpen && canHaveChildren(node) && !childrenMap[node.id] && !loadingIds.has(node.id)) {
      void loadChildren(node);
    }
  };

  const renderNode = (node: TreeNode) => {
    const expandable = canHaveChildren(node);
    const open = expanded.has(node.id);
    const kids = childrenMap[node.id];
    const isLoading = loadingIds.has(node.id);
    const hasError = errorIds.has(node.id);
    const selectable = isSelectable(node);
    const sel = selectable && !!selectedAreaCode && selectedAreaCode === node.areaCode;

    return (
      <div key={node.id}>
        <div
          className={[
            'flex items-center gap-2 rounded-lg px-1.5 py-1.5',
            sel ? 'bg-brand-shade' : 'hover:bg-brand-shade/60',
          ].join(' ')}
        >
          {expandable ? (
            <button
              type="button"
              onClick={() => toggleExpand(node)}
              aria-label={open ? 'Collapse' : 'Expand'}
              className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
            >
              <CaretIcon className={['size-4 transition-transform', open ? '' : '-rotate-90'].join(' ')} />
            </button>
          ) : (
            <span className="size-4 shrink-0" />
          )}
          <button
            type="button"
            onClick={() => {
              if (selectable) onSelect(node);
              else if (expandable) toggleExpand(node);
            }}
            className={[
              'min-w-0 flex-1 truncate text-left text-sm',
              sel ? 'font-semibold text-brand' : 'text-ink',
            ].join(' ')}
          >
            {node.label}
          </button>
          {EC_LEVEL[node.kind] && (
            <span className="shrink-0 rounded bg-[#f1f5f9] px-1.5 py-0.5 text-[10px] font-medium text-[#64748b]">
              {EC_LEVEL[node.kind]!.name}
            </span>
          )}
        </div>

        {expandable && open && (
          <div className="ml-[15px] flex flex-col border-l border-dashed border-[#cbd5e1] pl-3">
            {node.groupLabel && (
              <p className="py-1 pl-1 text-xs font-medium text-[#94a3b8]">{node.groupLabel}</p>
            )}
            {isLoading && (
              <div className="flex items-center gap-2 py-2 pl-1 text-xs text-[#94a3b8]">
                <Spinner size={14} /> Loading…
              </div>
            )}
            {hasError && (
              <div className="flex items-center gap-2 py-2 pl-1 text-xs text-red-500">
                Failed to load.
                <button
                  type="button"
                  onClick={() => void loadChildren(node)}
                  className="font-semibold underline"
                >
                  Retry
                </button>
              </div>
            )}
            {!isLoading && !hasError && kids && kids.length === 0 && (
              <p className="py-2 pl-1 text-xs text-[#94a3b8]">No items found.</p>
            )}
            {!isLoading && !hasError && kids?.map(renderNode)}
          </div>
        )}
      </div>
    );
  };

  if (!roots.length) {
    return (
      <p className="px-3 py-2 text-sm text-ink-secondary">
        No Election Commission boundary set for this organization.
      </p>
    );
  }
  return <div className="flex flex-col gap-0.5">{roots.map(renderNode)}</div>;
}
